import React, { useCallback, useLayoutEffect } from 'react';

import { useCart } from '../../hooks';
import type { CustomizationEditScreenProps } from '../../navigation/AppNavigation.props';
import { Customization } from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const CustomizationEditScreen = (
  props: CustomizationEditScreenProps,
) => {
  const { navigation, route } = props;

  const { restaurantSlug, productSlug, lineItemId } = route.params ?? {};

  const { cart } = useCart();

  // ─────────────────────────────────────────────────────────────────

  const navigateToBag = useCallback(() => {
    const previousRoutes = navigation.getState().routes;
    const previousRoute = previousRoutes.at(-2);

    // When navigating to the bag, if the bag is already in the stack, just return to it.
    if (navigation.canGoBack() && previousRoute?.name === 'Bag') {
      navigation.goBack();

      return;
    }

    navigation.replace('Bag');
  }, [navigation]);

  const navigateToCustomizationScreen = useCallback(() => {
    navigation.replace('Customization', { restaurantSlug, productSlug });
  }, [navigation, productSlug, restaurantSlug]);

  // ─────────────────────────────────────────────────────────────────

  // redirect to the customization screen if the line item isn't in the cart or has been removed
  useLayoutEffect(() => {
    const lineItems = cart?.lineItems;
    const isLineItemInCart = lineItems?.some(({ id }) => id === lineItemId);

    if (isLineItemInCart) return;

    navigateToCustomizationScreen();
  }, [cart?.lineItems, lineItemId, navigateToCustomizationScreen]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Customization
      restaurantSlug={restaurantSlug}
      productSlug={productSlug}
      lineItemId={lineItemId}
      isEditProductMode={true}
      onEditLineItemInCart={navigateToBag}
      onRemoveLineItemFromCart={navigateToBag}
    />
  );
};
